import React from 'react';
import { AuthenticatedImage } from './AuthenticatedImage';
import { AuthenticatedVideo, isVideoArtifact } from './AuthenticatedVideo';
import { getApiBaseUrl } from '../../config/environment';

interface RecentHistoryProps {
  jobs: Array<{
    jobId: string;
    workflowId: string;
    workflowName: string;
    createdAt: string;
    artifacts: Array<{
      filename: string;
      type: string;
    }>;
    thumbnailUrl?: string;
  }>;
  selectedJobId?: string;
  onSelectJob: (job: any) => void;
  accessToken: string;
}

const RecentHistory: React.FC<RecentHistoryProps> = ({ 
  jobs, 
  selectedJobId, 
  onSelectJob,
  accessToken 
}) => {
  // Get image URL with auth token
  const getImageUrl = (jobId: string, filename: string): string => {
    const baseUrl = getApiBaseUrl();
    return `${baseUrl}/api/jobs/${jobId}/artifacts/${filename}`;
  };

  // Format time ago
  const formatTimeAgo = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  if (jobs.length === 0) {
    return (
      <div className="h-24 sm:h-28 bg-surface-panel border-t border-gray-200 flex items-center justify-center">
        <div className="text-center">
          <svg className="w-8 h-8 mx-auto text-gray-300 mb-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
          </svg>
          <p className="text-meta text-gray-400">No recent images</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-32 sm:h-36 bg-surface-panel border-t border-gray-200 overflow-hidden flex flex-col">
      <div className="px-6 py-2.5 border-b border-gray-100 flex-shrink-0">
        <h3 className="text-label font-semibold text-gray-600">Recent Images</h3>
      </div>
      <div className="px-4 sm:px-6 py-3 overflow-x-auto flex-1 scrollbar-touch">
        <div className="flex gap-3 h-full">
          {jobs.map((job) => {
            const isSelected = job.jobId === selectedJobId;
            const primary = job.artifacts[0];
            const mediaUrl = primary ? getImageUrl(job.jobId, primary.filename) : '';
            const isVideo = !!primary && (isVideoArtifact(primary.filename) || isVideoArtifact(primary.type));

            return (
              <button
                key={job.jobId}
                type="button"
                onClick={() => onSelectJob(job)}
                className="flex-shrink-0 group relative touch-target"
              >
                <div className={`w-20 h-20 bg-gray-100 rounded-lg overflow-hidden transition-all duration-base relative ${
                  isSelected
                    ? 'ring-2 ring-blue-500 ring-offset-2 shadow-md'
                    : 'hover:ring-2 hover:ring-gray-300 hover:ring-offset-1 hover:shadow-sm'
                }`}>
                  {mediaUrl && isVideo ? (
                    <>
                      {/* Auth-fetched video — shown as a still poster (preload=metadata) with a play overlay */}
                      <AuthenticatedVideo
                        src={mediaUrl}
                        accessToken={accessToken}
                        controls={false}
                        muted
                        className="w-full h-full object-cover bg-black pointer-events-none"
                      />
                      <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/30 transition-colors duration-fast">
                        <svg className="w-7 h-7 text-white drop-shadow" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      </div>
                    </>
                  ) : mediaUrl ? (
                    <AuthenticatedImage
                      src={mediaUrl}
                      alt={`Generated ${job.workflowName}`}
                      accessToken={accessToken}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <svg className="w-6 h-6 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                  )}
                </div>
                {/* Time badge — always visible, increases in opacity on hover */}
                <div className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 bg-gray-800 text-white text-[10px] font-medium px-1.5 py-0.5 rounded-full whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-fast">
                  {formatTimeAgo(job.createdAt)}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default RecentHistory;

